import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { listDirectorDecisions } from '@/lib/messages.ts';
import type {
  ConversationOverview,
  DirectorDecision,
  Message,
} from '@/types/messages.ts';
import {
  placeDirectorDecisions,
  type PlacedDirectorDecisions,
} from './placeDirectorDecisions.ts';

/**
 * The director's decisions for the spoken practices on screen, placed in the
 * transcript. Only roleplay conversations have a director, so nothing else is asked.
 */
export function useDirectorDecisions(
  messages: Message[],
  conversations: ConversationOverview[],
): PlacedDirectorDecisions {
  const roleplayIds = useMemo(() => {
    const onScreen = new Set(messages.map((message) => message.conversation_id));
    return conversations
      .filter(
        (conversation) =>
          conversation.agent === 'roleplay' && onScreen.has(conversation.id),
      )
      .map((conversation) => conversation.id);
  }, [messages, conversations]);

  const decisions = useQueries({
    queries: roleplayIds.map((id) => ({
      queryKey: ['directorDecisions', id],
      queryFn: () => listDirectorDecisions(id),
    })),
    combine: allDecisions,
  });

  return useMemo(
    () => placeDirectorDecisions(messages, decisions),
    [messages, decisions],
  );
}

/* Outside the hook so its reference holds, which is what keeps the combined list stable. */
const allDecisions = (results: { data?: DirectorDecision[] }[]): DirectorDecision[] =>
  results.flatMap((result) => result.data ?? []);
